<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { TagFilter } from 'types';
import {
  SelectContent,
  SelectItem,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport
} from 'reka-ui';

const layout = useLayout();
const { t } = useI18n();

defineProps<{
  filters: TagFilter[];
}>();

const emit = defineEmits(['update:filter']);

const selectedFilter = ref<string>(useFilters().tags || t('General.AllSubjects'));
const selectOpen = ref<boolean>(false);

function updateFilter(tagFilter?: string) {
  selectedFilter.value = tagFilter ?? t('General.AllSubjects');

  const emittedTagFilter = tagFilter === t('General.AllSubjects') ? undefined : tagFilter;

  emit('update:filter', emittedTagFilter);
}
</script>

<template>
  <section>
    <div
      v-if="layout.isDesktop"
      class="flex justify-center py-1"
    >
      <ul
        :aria-label="$t('Aria.NewsListPage.FilterSubjects')"
        class="flex flex-row items-center gap-12 rounded-full bg-gray-25 p-1"
      >
        <li>
          <button
            :aria-pressed="selectedFilter === $t('General.AllSubjects')"
            class="cursor-pointer rounded-full px-4 py-2 text-base transition-[colors,shadow] duration-150 ease-in-out"
            :class="
              selectedFilter === $t('General.AllSubjects')
                ? 'bg-white text-black shadow-md shadow-black/15'
                : 'text-gray-filter-dark hover:bg-white/40 hover:text-black'
            "
            @click="updateFilter()"
          >
            {{ $t('General.AllSubjects') }}
          </button>
        </li>
        <li
          v-for="(filter, index) in filters"
          :key="`${filter.encodedValue}-${index}`"
        >
          <button
            :aria-pressed="selectedFilter === filter.encodedValue"
            class="text-xl cursor-pointer rounded-full px-4 py-2 text-base transition-[colors,shadow] duration-150 ease-in-out"
            :class="
              selectedFilter === filter.encodedValue
                ? 'bg-white text-black shadow-md shadow-black/15'
                : 'text-gray-filter-dark hover:bg-white/40 hover:text-black'
            "
            @click="updateFilter(filter.encodedValue)"
          >
            {{ filter.displayValue }}
          </button>
        </li>
      </ul>
    </div>

    <div v-else>
      <SelectRoot
        v-model:model-value="selectedFilter"
        v-model:open="selectOpen"
        @update:model-value="updateFilter"
      >
        <SelectTrigger
          :aria-label="$t('Aria.NewsListPage.FilterSubjects')"
          class="flex items-center gap-1 rounded-full bg-white px-4 py-2 text-base shadow-md shadow-black/15 outline-primary"
        >
          <SelectValue />
          <SelectIcon>
            <BaseIcon
              prefix="arrows"
              name="chevron-down"
              class="transition-transform"
              :class="{ 'rotate-180': selectOpen }"
            />
          </SelectIcon>
        </SelectTrigger>

        <SelectPortal>
          <SelectContent
            side="bottom"
            :side-offset="8"
            position="popper"
          >
            <SelectViewport
              class="grid gap-4 rounded-2xl bg-white p-4 text-base shadow-md shadow-black/15"
              style="min-width: var(--reka-select-trigger-width)"
            >
              <SelectItem
                :value="$t('General.AllSubjects')"
                class="outline-primary"
              >
                <SelectItemText>
                  {{ $t('General.AllSubjects') }}
                </SelectItemText>
              </SelectItem>
              <SelectItem
                v-for="(filter, index) in filters"
                :key="`${filter.encodedValue}-${index}`"
                :value="filter.encodedValue"
                class="outline-primary"
              >
                <SelectItemText>
                  {{ filter.displayValue }}
                </SelectItemText>
              </SelectItem>
            </SelectViewport>
          </SelectContent>
        </SelectPortal>
      </SelectRoot>
    </div>
  </section>
</template>
